import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Stripe Payment Links
const STRIPE_LINKS = {
  lite: 'https://buy.stripe.com/9B69AU82x9JPfPe2w4',
  business: 'https://buy.stripe.com/aFaaEY6Yt5tz0Ukb2A',
  enterprise: 'https://buy.stripe.com/aFadRabeJ1dj7iIgmU'
};

const PricingSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSubscribe = (plan) => {
    let url = STRIPE_LINKS[plan];
    if (user?.email) {
      url += `?prefilled_email=${encodeURIComponent(user.email)}`;
    }
    window.open(url, '_blank');
  };

  const plans = [
    {
      id: 'lite',
      name: 'Lite',
      price: 29,
      conversations: '500 conversas/mês',
      description: 'Para começar',
      features: ['Website chat', 'QR Codes', 'Flow Builder básico'],
      highlighted: false
    },
    {
      id: 'business',
      name: 'Business',
      price: 39,
      conversations: '1,000 conversas/mês',
      description: 'Mais popular',
      features: ['WhatsApp + Instagram', 'ChatGPT integrado', 'Todos os canais', 'Broadcasts'],
      highlighted: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 400,
      conversations: '15,000 conversas/mês',
      description: 'Para grandes operações',
      features: ['Tudo do Business', 'Suporte 24/7', 'Gerente dedicado'],
      highlighted: false
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Preços simples e transparentes
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Escolha o plano ideal para automatizar seu atendimento
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl shadow-lg border overflow-hidden transition-transform hover:scale-105 ${
                plan.highlighted ? 'border-blue-500 border-2 relative' : 'border-gray-200'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-center text-xs py-1 font-medium">
                  Mais Popular
                </div>
              )}

              <div className={`p-6 ${plan.highlighted ? 'pt-8' : ''}`}>
                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-500">{plan.description}</p>

                <div className="mt-4 mb-2">
                  <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-500">/mês</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">{plan.conversations}</p>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  className={`w-full py-3 font-semibold rounded-lg ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600'
                      : plan.id === 'enterprise'
                      ? 'bg-slate-900 hover:bg-slate-800'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.id === 'enterprise' ? 'Falar com vendas' : 'Começar grátis'}
                </Button>

                <ul className="mt-6 space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* See all plans */}
        <div className="mt-10 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/pricing')}
            className="text-blue-600 hover:text-blue-700"
          >
            Ver comparação completa dos planos
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
